import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  useAssetManager,
  useAssetManagerState,
  useAssetManagerDispatch,
} from "../AssetManager/AssetManagerContext/AssetManagerProvider";
import DragContainer from "./AssetUploader/DragContainer";
import { ReactComponent as UploadPlusIcon } from "../../assets/icons/AssetManager/add-gray-version-icon.svg";
import AssetPreviewer from "./AssetPreviewer";
import { ASSET_TYPE_ID } from "../../const";
import { DEFAULT_MEDIA_MIME_TYPES, mimeTypesMapping } from "../constants/mimeTypes";
import ConfirmActionModal from "../Modals/AssetUpdateMedallion/ConfirmActionModal";
import genericRepositoryService from "../../services/genericRepositoryService";
import ChangesConfirmedModal from "../Modals/AssetUpdateMedallion/ChangesConfirmedModal";
import useGlobalLoading from "../../hooks/useGlobalLoading";
import { prepareSmartObjectForUpload } from "../../lib/SmartObject/upload";

interface Props {
  onClose: () => void;
}

const isKeyOfMimeTypesMapping = (key: string | number | symbol): key is keyof typeof mimeTypesMapping =>
  key in mimeTypesMapping;

function hasThumbnailPreview(assetType: number) {
  return [ASSET_TYPE_ID.IMAGE, ASSET_TYPE_ID.PANORAMIC_IMAGE, ASSET_TYPE_ID.SMART_OBJECT].includes(assetType);
}

function getAllowedMimeType(assetType: number) {
  if (isKeyOfMimeTypesMapping(assetType)) {
    return mimeTypesMapping[assetType];
  }

  return DEFAULT_MEDIA_MIME_TYPES;
}

type RelatedLesson = {
  lessonVersionId: number;
  lessonName: string;
};

const AssetUpdateMedallion: React.FunctionComponent<Props> = ({ onClose }) => {
  const state = useAssetManager();
  const { assetLibraryDisplayedAssetType } = useAssetManagerState();
  const { withLoading } = useGlobalLoading();
  const dispatch = useAssetManagerDispatch();

  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const multiFileRef = useRef<HTMLInputElement>(null);
  const [assetFile, setAssetFile] = useState<File>();
  const [clickedFileUpload, setClickedFileUpload] = useState(false);
  const [uploadsProcessing, setUploadsProcessing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [newVersionNotes, setNewVersionNotes] = useState("");
  const [openConfirmActionModal, setOpenConfirmActionModal] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [isRelatedLessonsVisible, setIsRelatedLessonsVisible] = useState(false);
  const [relatedLessons, setRelatedLessons] = useState<RelatedLesson[]>();
  const [isUploading, setIsUploading] = useState(false);

  const selectedAsset = state.currentSelectedAsset;
  console.log("selectedAsset", selectedAsset);

  useEffect(() => {
    const fetchRelatedLessons = async () => {
      const response = await fetch(`/api/Asset/${selectedAsset.assetId}/lessons`);
      const data = (await response.json()) as { lessons: RelatedLesson[] };

      setRelatedLessons(data.lessons);
    };

    fetchRelatedLessons();
  }, [selectedAsset]);

  const dragEnter = () => {
    setDragging(true);
  };
  const dragLeave = () => {
    setDragging(false);
  };
  const dragOver = () => {
    setDragging(true);
  };

  const changeEffectUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setUploadsProcessing(true);
      if (clickedFileUpload) {
        addLocalFile(event.target.files);
        setClickedFileUpload(false);
      }
      setUploadsProcessing(false);
    }
  };

  function addLocalFile(files: FileList) {
    const [first] = Array.from(files);
    if (!first) return;

    // Single file for update flow
    setAssetFile(first);
    // Revoke previous preview URL if any
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    const url = URL.createObjectURL(first);
    setPreviewUrl(url);

    setDragging(false);
  }

  const drop = (event: React.DragEvent<HTMLInputElement>) => {
    if (event.dataTransfer) {
      setClickedFileUpload(false);
      setUploadsProcessing(true);
      addLocalFile(event.dataTransfer.files);
      setUploadsProcessing(false);
    }
  };

  function handleSaveAsset() {
    setOpenConfirmActionModal(true);
  }

  function handlePersistAsset(file: File, assetVersionId: number) {
    const handleUpdateAsset = async () => {
      setIsUploading(true);
      if (isUploading) return;
      try {
        const adjustedFileName = file.name.replace(/\s+/g, "_");
        const formData = new FormData();
        formData.append("versionNotes", newVersionNotes);
        formData.append("assetVersionId", assetVersionId.toString());
        formData.append("assetTypeName", selectedAsset.assetTypeName ?? "");
        formData.append("assetTypeId", assetLibraryDisplayedAssetType.toString());

        /**
         * The process for uploading a smart object is different.
         * We expect a .zip with the following files:
         * - request.json ? might be optional for updating.
         * - file.svg - original svg file (used for previews on asset library manager)
         * - smartObject.json - (actual SMO used in page manifest)
         * - settings.json (json file with the name and types of the files)
         */
        if (selectedAsset.assetTypeName === "Smart Object") {
          const { zipFile, zipFileName } = await prepareSmartObjectForUpload(file);
          formData.append("file", zipFile, zipFileName);
        } else {
          formData.append("file", file, adjustedFileName);
        }

        await genericRepositoryService.updateAssetVersion(formData);

        setSuccessMessageVisible(true);
        setOpenConfirmActionModal(false);

        dispatch({ type: "BUMP_ASSET_LIBRARY_REFRESH", payload: undefined });
      } catch (err) {
        console.error(err);
      } finally {
        setIsUploading(false);
      }
    };

    withLoading(handleUpdateAsset());
  }

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (!selectedAsset) return null;

  const withPreview = hasThumbnailPreview(assetLibraryDisplayedAssetType);
  const allowedMimeType = getAllowedMimeType(assetLibraryDisplayedAssetType);

  const modalContent = (
    <div className="assetManager-backdrop" style={{ zIndex: 9999 }}>
      <div className="assetManager-content-modal" style={{ zIndex: 10000 }}>
        <div className="assetManager-title">
          <p>Asset Library Manager</p>
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onClose();
            }}
          >
            <svg width="17px" height="17px" viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg">
              <title>Remove</title>
              <g id="New-Settings-references" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                <g id="Tags-when-uploading-1" transform="translate(-5297.000000, -505.000000)" stroke="#FFFFFF">
                  <g id="Group-28" transform="translate(5297.000000, 505.000000)">
                    <circle onClick={onClose} className="svg-icon-bg" cx="8.5" cy="8.5" r="8"></circle>
                    <g
                      id="Group-13-Copy-10"
                      transform="translate(5.000000, 5.000000)"
                      strokeLinecap="square"
                      strokeWidth="2"
                    >
                      <line x1="0.291666667" y1="0.291666667" x2="6.70833333" y2="6.70833333" id="Line-4"></line>
                      <line
                        x1="0.291666667"
                        y1="0.291666667"
                        x2="6.70833333"
                        y2="6.70833333"
                        id="Line-4-Copy"
                        transform="translate(3.500000, 3.500000) scale(-1, 1) translate(-3.500000, -3.500000) "
                      ></line>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </div>
        </div>

        <div className="assetManager-library-upload-choose">
          <div className="upload-choose-buttons-container">
            <div
              className="asset-update-medallion-library-button"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onClose();
              }}
              onClick={onClose}
            >
              <p>Library</p>
            </div>
            <div className="asset-update-medallion-update-button">
              <p>
                <UploadPlusIcon /> &nbsp; Update Asset
              </p>
            </div>
          </div>
        </div>

        <div className="update-asset">
          <div className="uploader-section">
            <div className="uploaded-header">
              <p style={{ margin: "0 0 0 1%" }}>
                <strong>Asset To Update (Review)</strong>
              </p>
            </div>

            <div className="asset-details">
              <AssetPreviewer readonly thumbnail={withPreview} />
            </div>
          </div>
          <div aria-label="Related lessons" className="lesson-list-elements">
            <button
              onClick={() => {
                setIsRelatedLessonsVisible(!isRelatedLessonsVisible);
              }}
            >
              <span>Lessons Where The Asset Is</span>
              <span>{isRelatedLessonsVisible ? "\u25B2" : "\u25BC"}</span>
            </button>

            {isRelatedLessonsVisible && (
              <ul className="lesson-list-elements">
                {relatedLessons?.length === 0 ? (
                  <li className="no-lessons-message">No lessons found</li>
                ) : (
                  relatedLessons?.map((lesson) => (
                    <li key={lesson.lessonVersionId} className="lesson-row">
                      {lesson.lessonName}
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          <div className="uploader-section">
            <div className="uploaded-header">
              <p style={{ margin: "0 0 0 1%" }}>
                <strong>Upload New Version</strong>
              </p>
            </div>
            <div className="asset-update-upload">
              {!assetFile && (
                <DragContainer
                  dragDiv={dragRef}
                  multiFile={multiFileRef}
                  dragEnter={dragEnter}
                  dragLeave={dragLeave}
                  dragOver={dragOver}
                  drop={drop}
                  changeEffectUpload={changeEffectUpload}
                  setClickedFileUpload={setClickedFileUpload}
                  setUploadsProcessing={setUploadsProcessing}
                  newFiles={assetFile}
                  setNewFiles={setAssetFile}
                  dragging={dragging}
                  fileString={allowedMimeType}
                  multiple={false}
                />
              )}
              {previewUrl && (
                <AssetPreviewer
                  previewUrl={previewUrl}
                  newVersionNotes={newVersionNotes}
                  setNewVersionNotes={setNewVersionNotes}
                  thumbnail={withPreview}
                />
              )}
              {uploadsProcessing && <p style={{ marginTop: 8 }}>Processing...</p>}
            </div>
          </div>
        </div>

        <div className="assetManager-buttons-container">
          <button onClick={onClose}>Cancel</button>
          <button disabled={!assetFile} onClick={handleSaveAsset}>
            Save
          </button>
        </div>
      </div>
    </div>
  );

  const confirmActionModalContent = (
    <ConfirmActionModal
      onClose={() => {
        setOpenConfirmActionModal(false);
        onClose();
      }}
      onContinue={() => {
        if (assetFile && selectedAsset.assetId && !isUploading) {
          handlePersistAsset(assetFile, selectedAsset.assetId);
        }
      }}
      open
    />
  );

  const successMessageContent = (
    <ChangesConfirmedModal
      onClose={() => {
        setSuccessMessageVisible(false);
        onClose();
      }}
      open
    />
  );

  if (openConfirmActionModal) {
    return ReactDOM.createPortal(confirmActionModalContent, document.body);
  }

  if (successMessageVisible) {
    return ReactDOM.createPortal(successMessageContent, document.body);
  }

  return ReactDOM.createPortal(modalContent, document.body);
};

export default AssetUpdateMedallion;
